import { Button, Paper, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { Add, Delete, Publish } from "@material-ui/icons";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { BooleanInput, Button as RaButton, Confirm, EditButton } from "react-admin";
import { Field, useForm, useFormState } from "react-final-form";
import * as XLSX from "xlsx";
import { RestrictedScheduleDialog } from "./restricted-schedule-dialog";

const parseRestrictedScheduleXlsx = (arrayBuffer) => {
  // raw: false returns the formatted display string (w field) rather than a Date object,
  // making date/time parsing timezone-independent.
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

  return rows.map((row) => ({
    type: row.type.split(",").map((s) => s.trim().toLowerCase()),
    region: row.region.split(",").map((s) => s.trim().toUpperCase()),
    start_date: String(row.start_date).trim(),
    start_time: String(row.start_time).trim(),
    end_date: String(row.end_date).trim(),
    end_time: String(row.end_time).trim(),
  }));
};

const DeleteButton = ({ onDelete }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <>
      <RaButton
        label="Delete"
        startIcon={<Delete />}
        style={{ color: "red" }}
        onClick={() => {
          setShowConfirm(true);
        }}
      />
      <Confirm
        isOpen={showConfirm}
        title="Are you sure you want to delete this item?"
        onConfirm={async () => {
          onDelete();
          setShowConfirm(false);
        }}
        onClose={() => {
          setShowConfirm(false);
        }}
      />
    </>
  );
};

export const RestrictedScheduleTable = () => {
  const form = useForm();
  const { values } = useFormState();
  const fileInputRef = useRef(null);
  const [schedules, setSchedules] = useState(values.restricted_schedule || []);
  const [dialogState, setDialogState] = useState({ open: false, mode: "create", index: null });

  useEffect(() => {
    setSchedules(values.restricted_schedule || []);
  }, [values.restricted_schedule]);

  const updateSchedules = (nextSchedules) => {
    setSchedules(nextSchedules);
    form.change("restricted_schedule", nextSchedules);
    form.change("restricted_schedule_updated", true);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const parsed = parseRestrictedScheduleXlsx(evt.target.result);
      updateSchedules(parsed);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleDialogSave = (schedule) => {
    if (dialogState.mode === "edit") {
      const nextSchedules = [...schedules];
      nextSchedules[dialogState.index] = schedule;
      updateSchedules(nextSchedules);
    } else {
      updateSchedules([schedule, ...schedules]);
    }

    setDialogState({ open: false, mode: "create", index: null });
  };

  return (
    <div>
      <Field name="restricted_schedule_updated" component="input" type="hidden" initialValue={false} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>Restricted schedule</span>
          <BooleanInput source="listen_copyright_schedules" label="Enabled" defaultValue={true} style={{ marginLeft: 12, marginBottom: -6 }} />
        </div>
        <div>
          <Button color="primary" startIcon={<Add />} onClick={() => setDialogState({ open: true, mode: "create", index: null })}>
            Create
          </Button>
          <Button color="primary" startIcon={<Publish />} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
            Import
          </Button>
        </div>
        <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: "none" }} onChange={handleImport} />
      </div>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Start time</TableCell>
              <TableCell>End time</TableCell>
              <TableCell>Restricted Region</TableCell>
              <TableCell>Type</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{`${row.start_date} ${row.start_time}`.trim()}</TableCell>
                <TableCell>{`${row.end_date} ${row.end_time}`.trim()}</TableCell>
                <TableCell>{(row.region || []).join(", ")}</TableCell>
                <TableCell>{(row.type || []).map((t) => _.capitalize(t)).join(", ")}</TableCell>
                <TableCell>
                  <EditButton onClick={() => setDialogState({ open: true, mode: "edit", index: idx })} />
                </TableCell>
                <TableCell>
                  <DeleteButton onDelete={() => updateSchedules(schedules.filter((_, rowIdx) => rowIdx !== idx))} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <RestrictedScheduleDialog open={dialogState.open} mode={dialogState.mode} initialValue={dialogState.index === null ? null : schedules[dialogState.index]} onClose={() => setDialogState({ open: false, mode: "create", index: null })} onSave={handleDialogSave} />
    </div>
  );
};
